import { useRef } from 'react';
import { useFramework, useIdentity, useScale } from '@/framework';
import { convertLayerInfoToPoints, getLayerInfoWithRotate } from '@/utils/layer-kit';
import {
    DirectionType,
    ResizeOperateType,
    ScaleOperateType,
    type ILayerProps,
    type IPoint,
    type ITransform,
} from '@/version-types';
import { REFERENCE_LINE_DIRECTION } from '../constants';
import { getReferenceLinesState } from '../states/reference-lines-state';
import type { ReferenceLine } from '../types';
import {
    MOVING_REFERENCE_LINE_ABSORB_DISTANCE,
    RESIZING_REFERENCE_LINE_ABSORB_DISTANCE,
    SCALE_REFERENCE_LINE_ABSORB_DISTANCE,
} from './constants';
import { getDirectionVector, getResizeAbsorbOffset, getScaleAbsorbOffset } from './utils';

interface NearestLine {
    /** 当前图层到参考线的距离，单位为编辑器逻辑坐标。 */
    distance: number;
    /** 吸附后当前图层或操作点应处于的坐标。 */
    position: number;
}

const outOfRange = (value: number, range: readonly number[]) => value <= range[0] || value >= range[1];

const getNearest = (lines: NearestLine[]) =>
    lines.reduce<NearestLine | undefined>((nearest, line) => {
        if (!nearest || Math.abs(line.distance) < Math.abs(nearest.distance)) {
            return line;
        }
        return nearest;
    }, undefined);

const getMoveNearestLines = (layer: ILayerProps, referenceLines: readonly ReferenceLine[]) => {
    const layerRect = getLayerInfoWithRotate(layer);
    const points = convertLayerInfoToPoints(layerRect);
    const vertical: NearestLine[] = [];
    const horizontal: NearestLine[] = [];

    referenceLines.forEach((line) => {
        if (line.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
            [points.lt, points.tc, points.rt].forEach((point, index) => {
                vertical.push({
                    distance: Math.floor(point.x - line.position),
                    position: Math.floor(line.position - ~~(index === 0 ? 0 : layerRect.width / (3 - index))),
                });
            });
        } else {
            [points.lt, points.lc, points.lb].forEach((point, index) => {
                horizontal.push({
                    distance: Math.floor(point.y - line.position),
                    position: Math.floor(line.position - ~~(index === 0 ? 0 : layerRect.height / (3 - index))),
                });
            });
        }
    });

    return {
        vertical: getNearest(vertical),
        horizontal: getNearest(horizontal),
    };
};

const getResizeNearestLines = (
    layer: ITransform,
    referenceLines: readonly ReferenceLine[],
    directionVector: IPoint,
) => {
    const layerRect = getLayerInfoWithRotate(layer);
    const points = convertLayerInfoToPoints(layerRect);
    const xPoint = directionVector.x === 0 ? Infinity : directionVector.x > 0 ? points.rt.x : points.lt.x;
    const yPoint = directionVector.y === 0 ? Infinity : directionVector.y > 0 ? points.rb.y : points.rt.y;
    const vertical: NearestLine[] = [];
    const horizontal: NearestLine[] = [];

    referenceLines.forEach((line) => {
        if (line.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
            vertical.push({
                distance: xPoint - line.position,
                position: line.position,
            });
        } else {
            horizontal.push({
                distance: yPoint - line.position,
                position: line.position,
            });
        }
    });

    return {
        vertical: getNearest(vertical),
        horizontal: getNearest(horizontal),
    };
};

export const useReferenceLineSnap = () => {
    const framework = useFramework();
    const identity = useIdentity();
    const scale = useScale();
    const scaleRef = useRef(scale);
    scaleRef.current = scale;
    const referenceLines = useRef<ReferenceLine[]>([]);

    const isMoveSnapX = useRef(false);
    const isMoveSnapY = useRef(false);
    const moveRangeX = useRef<[number, number]>([0, 0]);
    const moveRangeY = useRef<[number, number]>([0, 0]);
    const moveDisplacement = useRef<IPoint>({ x: 0, y: 0 });

    const isResizeSnap = useRef(false);
    const resizeDisplacement = useRef<IPoint>({ x: 0, y: 0 });
    const isScaleSnap = useRef(false);
    const scaleDisplacement = useRef<IPoint>({ x: 0, y: 0 });

    const prepare = () => {
        reset();
        if (framework.service.edgelessCanvas.isEdgelessMode) return;

        const stageIndex = framework.service.frame.getActiveIndex();
        const stageTop = framework.service.frame.getStageTop(stageIndex);
        referenceLines.current = getReferenceLinesState(identity).referenceLines.map((line) =>
            line.direction === REFERENCE_LINE_DIRECTION.HORIZONTAL
                ? { ...line, position: line.position - stageTop }
                : line,
        );
    };

    const move = ({
        rawX,
        rawY,
        oldLayer,
        currentLayer,
    }: {
        rawX: number;
        rawY: number;
        oldLayer: ILayerProps;
        currentLayer: ILayerProps;
    }) => {
        if (!referenceLines.current.length) {
            moveDisplacement.current = { x: rawX, y: rawY };
            return {
                ...moveDisplacement.current,
                isSnapX: false,
                isSnapY: false,
            };
        }

        const nearest = getMoveNearestLines(currentLayer, referenceLines.current);
        const originRect = getLayerInfoWithRotate(oldLayer);
        const absorbDistance = Math.max(1, Math.ceil(MOVING_REFERENCE_LINE_ABSORB_DISTANCE / scaleRef.current));

        if (nearest.vertical) {
            const targetX = nearest.vertical.position - originRect.left;
            if (Math.abs(nearest.vertical.distance) < absorbDistance && !isMoveSnapX.current) {
                moveDisplacement.current.x = targetX;
                moveRangeX.current = [targetX - absorbDistance, targetX + absorbDistance];
                isMoveSnapX.current = true;
            }
            if (isMoveSnapX.current && outOfRange(rawX, moveRangeX.current)) {
                isMoveSnapX.current = false;
            }
        } else {
            isMoveSnapX.current = false;
        }
        if (!isMoveSnapX.current) moveDisplacement.current.x = rawX;

        if (nearest.horizontal) {
            const targetY = nearest.horizontal.position - originRect.top;
            if (Math.abs(nearest.horizontal.distance) < absorbDistance && !isMoveSnapY.current) {
                moveDisplacement.current.y = targetY;
                moveRangeY.current = [targetY - absorbDistance, targetY + absorbDistance];
                isMoveSnapY.current = true;
            }
            if (isMoveSnapY.current && outOfRange(rawY, moveRangeY.current)) {
                isMoveSnapY.current = false;
            }
        } else {
            isMoveSnapY.current = false;
        }
        if (!isMoveSnapY.current) moveDisplacement.current.y = rawY;

        return {
            ...moveDisplacement.current,
            isSnapX: isMoveSnapX.current,
            isSnapY: isMoveSnapY.current,
        };
    };

    const resize = ({
        rawDisplacementX,
        rawDisplacementY,
        oldLayer,
        currentLayer,
        operateType,
        direction,
    }: {
        rawDisplacementX: number;
        rawDisplacementY: number;
        oldLayer: ILayerProps;
        currentLayer: ILayerProps;
        operateType: ResizeOperateType;
        direction: DirectionType;
    }) => {
        if (!referenceLines.current.length) {
            resizeDisplacement.current = {
                x: rawDisplacementX,
                y: rawDisplacementY,
            };
            return { ...resizeDisplacement.current, isSnap: false };
        }

        const rotate = currentLayer.rotate;
        const isChangeHeight = operateType === ResizeOperateType.TC || operateType === ResizeOperateType.BC;
        const leftOffset = !isChangeHeight && direction === DirectionType.OPPOSITE ? rawDisplacementX : 0;
        const topOffset = isChangeHeight && direction === DirectionType.OPPOSITE ? rawDisplacementY : 0;
        const resizedLayer = {
            ...oldLayer,
            left: oldLayer.left + leftOffset,
            top: oldLayer.top + topOffset,
            width: Math.floor(oldLayer.width + rawDisplacementX * direction),
            height: Math.floor(oldLayer.height + rawDisplacementY * direction),
        };
        const oldLayerRect = getLayerInfoWithRotate(oldLayer);
        const centerPoint = {
            x: oldLayer.width / 2 + oldLayer.left,
            y: oldLayer.height / 2 + oldLayer.top,
        };
        const newLayerRect = getLayerInfoWithRotate(resizedLayer, centerPoint);
        const directionVector = getDirectionVector(operateType, rotate);
        const nearest = getResizeNearestLines({ ...newLayerRect, rotate: 0 }, referenceLines.current, directionVector);
        const absorbDistance = Math.max(1, Math.ceil(RESIZING_REFERENCE_LINE_ABSORB_DISTANCE / scaleRef.current));
        const nearestOffsetX = nearest.vertical ? -nearest.vertical.distance : Infinity;
        const nearestOffsetY = nearest.horizontal ? -nearest.horizontal.distance : Infinity;

        if (Math.abs(nearestOffsetX) < absorbDistance || Math.abs(nearestOffsetY) < absorbDistance) {
            if (!isResizeSnap.current) {
                let offsetX = Infinity;
                let offsetY = Infinity;

                if (Math.abs(nearestOffsetX) < absorbDistance) {
                    const directionX = Math.sign(directionVector.x);
                    const additionalRectSize =
                        Math.abs(newLayerRect.width + nearestOffsetX * directionX) - oldLayerRect.width;
                    offsetX = getResizeAbsorbOffset({
                        rotate,
                        isChangeHeight,
                        isHorizontal: false,
                        additionalRectSize,
                    });
                }
                if (Math.abs(nearestOffsetY) < absorbDistance) {
                    const directionY = Math.sign(directionVector.y);
                    const additionalRectSize =
                        Math.abs(newLayerRect.height + nearestOffsetY * directionY) - oldLayerRect.height;
                    offsetY = getResizeAbsorbOffset({
                        rotate,
                        isChangeHeight,
                        isHorizontal: true,
                        additionalRectSize,
                    });
                }

                const offset = Math.abs(offsetX) < Math.abs(offsetY) ? offsetX : offsetY;
                if (isChangeHeight) {
                    resizeDisplacement.current.y = offset * direction;
                } else {
                    resizeDisplacement.current.x = offset * direction;
                }
                isResizeSnap.current = Number.isFinite(offset);
            }
        } else {
            isResizeSnap.current = false;
        }

        if (!isResizeSnap.current) {
            resizeDisplacement.current = {
                x: rawDisplacementX,
                y: rawDisplacementY,
            };
        }

        return {
            ...resizeDisplacement.current,
            isSnap: isResizeSnap.current,
        };
    };

    const updateHighlight = (layer: ILayerProps) => {
        const rect = getLayerInfoWithRotate(layer);
        const left = rect.left;
        const right = rect.left + rect.width;
        const top = rect.top;
        const bottom = rect.top + rect.height;
        const centerX = left + ~~(rect.width / 2);
        const centerY = top + ~~(rect.height / 2);
        const tolerance = 1;

        const ids = referenceLines.current
            .filter((line) => {
                if (line.direction === REFERENCE_LINE_DIRECTION.VERTICAL) {
                    return [left, centerX, right].some((value) => Math.abs(value - line.position) < tolerance);
                }
                return [top, centerY, bottom].some((value) => Math.abs(value - line.position) < tolerance);
            })
            .map((line) => line.id);

        getReferenceLinesState(identity).setHighlightReferenceLineIds(ids);
    };

    /** 已迁入 reference-lines 的 scale 吸附实现；控制器当前不会调用。 */
    const scaleLayer = ({
        displacementX,
        displacementY,
        offsetX,
        offsetY,
        directionX,
        directionY,
        oldLayer,
        currentLayer,
        operateType,
        keepRatio,
    }: {
        displacementX: number;
        displacementY: number;
        offsetX: number;
        offsetY: number;
        directionX: DirectionType;
        directionY: DirectionType;
        oldLayer: ILayerProps;
        currentLayer: ILayerProps;
        operateType: ScaleOperateType;
        keepRatio: boolean;
    }) => {
        if (!referenceLines.current.length) {
            return { x: offsetX, y: offsetY, isSnap: false };
        }

        const rotate = currentLayer.rotate;
        const leftOffset = directionX === DirectionType.OPPOSITE ? displacementX * directionX : 0;
        const topOffset = directionY === DirectionType.OPPOSITE ? displacementY * directionY : 0;
        const resizedLayer = {
            ...oldLayer,
            left: oldLayer.left + leftOffset,
            top: oldLayer.top + topOffset,
            width: Math.floor(oldLayer.width + displacementX),
            height: Math.floor(oldLayer.height + displacementY),
        };
        const oldLayerRect = getLayerInfoWithRotate(oldLayer);
        const centerPoint = {
            x: oldLayer.width / 2 + oldLayer.left,
            y: oldLayer.height / 2 + oldLayer.top,
        };
        const newLayerRect = getLayerInfoWithRotate(resizedLayer, centerPoint);
        const directionVector = getDirectionVector(operateType, rotate);
        const nearest = getResizeNearestLines({ ...newLayerRect, rotate: 0 }, referenceLines.current, directionVector);
        const absorbDistance = Math.max(1, Math.ceil(SCALE_REFERENCE_LINE_ABSORB_DISTANCE / scaleRef.current));
        const nearestOffsetX = nearest.vertical ? -nearest.vertical.distance : Infinity;
        const nearestOffsetY = nearest.horizontal ? -nearest.horizontal.distance : Infinity;

        if (Math.abs(nearestOffsetX) < absorbDistance || Math.abs(nearestOffsetY) < absorbDistance) {
            if (!keepRatio) {
                if (Math.abs(nearestOffsetX) < Math.abs(nearestOffsetY)) {
                    scaleDisplacement.current = {
                        x: offsetX + nearestOffsetX,
                        y: offsetY,
                    };
                } else {
                    scaleDisplacement.current = {
                        x: offsetX,
                        y: offsetY + nearestOffsetY,
                    };
                }
                isScaleSnap.current = true;
            } else if (!isScaleSnap.current) {
                const isHorizontal = Math.abs(nearestOffsetY) <= Math.abs(nearestOffsetX);
                const directionValue = Math.sign(isHorizontal ? directionVector.y : directionVector.x);
                const rectSize = isHorizontal ? newLayerRect.height : newLayerRect.width;
                const oldRectSize = isHorizontal ? oldLayerRect.height : oldLayerRect.width;
                const nearestOffset = isHorizontal ? nearestOffsetY : nearestOffsetX;
                const additionalRectSize = Math.abs(rectSize + nearestOffset * directionValue) - oldRectSize;
                scaleDisplacement.current = getScaleAbsorbOffset({
                    rotate,
                    isHorizontal,
                    additionalRectSize,
                    oldLayer,
                    oldLayerRect,
                    mouseOffsetX: displacementX,
                    mouseOffsetY: displacementY,
                });
                isScaleSnap.current = true;
            }
        } else {
            scaleDisplacement.current = { x: offsetX, y: offsetY };
            isScaleSnap.current = false;
        }

        return {
            ...scaleDisplacement.current,
            isSnap: isScaleSnap.current,
        };
    };

    function reset() {
        referenceLines.current = [];
        isMoveSnapX.current = false;
        isMoveSnapY.current = false;
        isResizeSnap.current = false;
        isScaleSnap.current = false;
        getReferenceLinesState(identity).setHighlightReferenceLineIds([]);
    }

    return {
        prepare,
        move,
        resize,
        scaleLayer,
        updateHighlight,
        reset,
    };
};
